import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
   Box,
   Typography,
   Avatar,
   List,
   ListItem,
   ListItemAvatar,
   ListItemText,
   Badge,
   Chip,
   CircularProgress,
   TextField,
   IconButton,
   Tooltip,
   Button,
   InputAdornment,
   Collapse,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Card,
   CardContent,
   Fab,
   AppBar,
   Toolbar,
   Grid,
   Paper,
   Divider,
   Alert,
   Snackbar,
   useTheme,
   useMediaQuery,
} from '@mui/material';
import {
   Chat as ChatIcon,
   Search as SearchIcon,
   Send as SendIcon,
   Close as CloseIcon,
   Launch as LaunchIcon,
   Message as MessageIcon,
   Person as PersonIcon,
   Group as GroupIcon,
   Refresh as RefreshIcon,
   CheckCircle as CheckCircleIcon,
   Error as ErrorIcon,
   KeyboardArrowDown as KeyboardArrowDownIcon,
   KeyboardArrowUp as KeyboardArrowUpIcon,
   Add as AddIcon,
   CancelRounded as CancelRoundedIcon,
   Event as EventIcon,
   ArrowBack as ArrowBackIcon,
   KeyboardArrowUp as ExpandLessIcon,
   Minimize as MinimizeIcon,
} from '@mui/icons-material';
import { useWebSocketChat } from '../../contexts/WebSocketChatContext';
import { useUser } from '../../contexts/UserContext';
import { useFloatingChat } from '../../contexts/FloatingChatContext';

import axios from 'axios';
import { ChatMessage, ChatRoom } from '../../services/WebSocketService';
import onlineStatusService from '../../services/OnlineStatusService';
import { getAuthHeaders } from '../../utils/auth';
import OnlineIndicator from '../OnlineIndicator/OnlineIndicator';
import WebSocketChatView from '../WebSocketChat/WebSocketChatView';
import CreateGroupChatDialog from '../../pages/Chat/components/CreateGroupChatDialog';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';
import {
   collection,
   query,
   where,
   getDocs,
   limit,
   startAfter,
   orderBy,
   getDoc,
   doc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import webSocketService from '../../services/WebSocketService';

// Contact interface
interface Contact {
   id: string;
   userId: string;
   displayName: string;
   email: string;
   photoURL?: string;
   source?: string;
}

// Entity channel interface
interface EntityChannel {
   id: string;
   entityId: string;
   entityName: string;
   entityType: 'event' | 'trip';
   createdAt: Date;
   participants: string[];
}

// Pagination state interface
interface PaginationState {
   hasMore: boolean;
   lastDocs: {
      relationships?: any;
      eventInvitations?: any;
      tripInvitations?: any;
      myEventInvitations?: any;
      myTripInvitations?: any;
   };
}

// Custom channel preview component
const CustomChannelPreview = ({
   channel,
   setActiveChannel,
   activeChannel,
   currentUser,
   onAvatarClick,
   getChannelDisplayName,
   userProfiles,
}: {
   channel: ChatRoom;
   setActiveChannel: (channel: ChatRoom) => void;
   activeChannel: ChatRoom | null;
   currentUser: any;
   onAvatarClick?: (channel: ChatRoom) => void;
   getChannelDisplayName: (channel: ChatRoom) => string;
   userProfiles: { [userId: string]: { displayName: string; photoURL: string | null } };
}) => {
   const { messages } = useWebSocketChat();
   const { name } = channel;
   const unreadCount = channel.unreadCount || 0;

   // Get messages for this channel to help with name resolution
   const channelMessages = messages[channel.id] || [];

   // Get last message
   const lastMessage = channel.lastMessage;

   // Determine if it's a direct message or group chat
   const isDirectMessage = channel.isPrivate && channel.members.length === 2;

   const displayName = getChannelDisplayName(channel);

   // Get avatar URL for this channel
   let avatarUrl = channel.avatarUrl;

   if (isDirectMessage) {
      const otherUserId = channel.members.find((id) => id !== currentUser?.uid);

      if (otherUserId && userProfiles[otherUserId]) {
         avatarUrl = userProfiles[otherUserId].photoURL || avatarUrl;

         // If no avatar, create a fallback using initials
         if (!avatarUrl) {
            const initials = userProfiles[otherUserId].displayName
               .split(' ')
               .map((word) => word[0])
               .join('')
               .toUpperCase()
               .substring(0, 2);
            avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=random&color=fff&size=128`;
         }
      }
   }

   // Get initials for avatar
   const getInitials = (name: string) => {
      if (name.startsWith('Chat with ') || name === 'Direct Chat' || name === 'Group Chat') {
         return isDirectMessage ? 'DM' : 'GC';
      }
      return name
         .split(' ')
         .map((word) => word[0])
         .join('')
         .toUpperCase()
         .substring(0, 2);
   };

   // Format last message preview
   const formatLastMessage = () => {
      if (!lastMessage) return 'No messages yet';

      const isOwnMessage = lastMessage.senderId === currentUser?.uid;

      // For direct messages, don't show sender name prefix
      if (isDirectMessage) {
         const senderPrefix = isOwnMessage ? 'You: ' : '';
         return `${senderPrefix}${lastMessage.text}`;
      }

      // For group chats, use dynamic name resolution for sender
      const senderName =
         lastMessage.senderId === currentUser?.uid
            ? 'You'
            : (() => {
                 // Try to get the sender's name from messages first
                 const senderMessage = channelMessages.find(
                    (msg) => msg.senderId === lastMessage.senderId
                 );
                 if (
                    senderMessage &&
                    senderMessage.senderName &&
                    senderMessage.senderName !== 'Unknown User' &&
                    !senderMessage.senderName.startsWith('User ')
                 ) {
                    return senderMessage.senderName;
                 }
                 // Fallback to the last message sender name if it's valid
                 if (
                    lastMessage.senderName &&
                    lastMessage.senderName !== 'Unknown User' &&
                    !lastMessage.senderName.startsWith('User ')
                 ) {
                    return lastMessage.senderName;
                 }
                 // Final fallback
                 return `User ${lastMessage.senderId.substring(0, 8)}`;
              })();

      return `${senderName}: ${lastMessage.text}`;
   };

   // Format timestamp
   const formatTimestamp = () => {
      if (!lastMessage) return '';

      const now = new Date();
      const messageTime = lastMessage.timestamp;
      const diffInHours = (now.getTime() - messageTime.getTime()) / (1000 * 60 * 60);

      if (diffInHours < 24) {
         return messageTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else {
         return messageTime.toLocaleDateString();
      }
   };

   return (
      <ListItem
         sx={{
            borderRadius: 1,
            mb: 0.5,
            bgcolor: channel.id === activeChannel?.id ? 'action.selected' : 'transparent',
            '&:hover': {
               bgcolor: 'action.hover',
            },
            py: 1.5,
            px: 1,
         }}
      >
         <ListItemAvatar>
            <Badge
               overlap='circular'
               anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
               badgeContent={
                  isDirectMessage
                     ? (() => {
                          const otherUserId = channel.members.find((id) => id !== currentUser?.uid);
                          return otherUserId ? (
                             <OnlineIndicator userId={otherUserId} size='small' />
                          ) : null;
                       })()
                     : null
               }
            >
               <Tooltip title='Click to open floating chat'>
                  <Avatar
                     src={avatarUrl}
                     sx={{
                        bgcolor: isDirectMessage ? 'primary.main' : 'secondary.main',
                        cursor: 'pointer',
                        '&:hover': {
                           transform: 'scale(1.1)',
                           transition: 'transform 0.2s ease-in-out',
                        },
                     }}
                     onClick={(e) => {
                        e.stopPropagation();
                        if (onAvatarClick) {
                           onAvatarClick(channel);
                        }
                     }}
                  >
                     {isDirectMessage ? getInitials(displayName) : <GroupIcon />}
                  </Avatar>
               </Tooltip>
            </Badge>
         </ListItemAvatar>
         <ListItemText
            onClick={() => setActiveChannel(channel)}
            sx={{ cursor: 'pointer' }}
            primary={
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography
                     variant='body1'
                     fontWeight={unreadCount > 0 ? 'bold' : 'normal'}
                     noWrap
                     sx={{ maxWidth: '70%' }}
                  >
                     {displayName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     {formatTimestamp() && (
                        <Typography
                           variant='caption'
                           color='text.secondary'
                           fontWeight={unreadCount > 0 ? 'bold' : 'normal'}
                        >
                           {formatTimestamp()}
                        </Typography>
                     )}
                     {unreadCount > 0 && (
                        <Badge
                           badgeContent={unreadCount}
                           color='primary'
                           sx={{
                              '& .MuiBadge-badge': {
                                 fontSize: '0.7rem',
                                 minWidth: '18px',
                                 height: '18px',
                              },
                           }}
                        />
                     )}
                  </Box>
               </Box>
            }
            secondary={
               <Typography
                  variant='body2'
                  color='text.secondary'
                  noWrap
                  sx={{ maxWidth: '85%' }}
                  fontWeight={unreadCount > 0 ? 'bold' : 'normal'}
               >
                  {formatLastMessage()}
               </Typography>
            }
         />
      </ListItem>
   );
};

const GlobalFloatingChat: React.FC = () => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));

   const {
      floatingChats,
      floatingChatListOpen,
      floatingChatListMinimized,
      showMainChat,
      selectedContact,
      firstMessageModalOpen,
      firstMessage,
      sendingFirstMessage,
      existingChatMessage,
      showExistingChatMessage,

      openFloatingChat,
      closeFloatingChat,
      toggleFloatingChat,
      closeAllFloatingChats,
      openFloatingChatForContact,

      setFloatingChatListOpen,
      setFloatingChatListMinimized,
      setShowMainChat,

      setSelectedContact,
      setFirstMessageModalOpen,
      setFirstMessage,
      handleSendFirstMessage,
      handleCloseFirstMessageModal,
      setExistingChatMessage,
      setShowExistingChatMessage,
   } = useFloatingChat();

   const {
      channels,
      activeChannel,
      setActiveChannel,
      refreshChannels,
      loadMoreRooms,
      roomsPagination,
      clearCacheAndRefresh,
      cleanupInvalidRooms,
      loading,
      error,
      client,
      createOneOnOneChat,
      sendMessage,
      isFallbackMode,
      connectionStatus,
      messages,
      loadRoomMessages,
   } = useWebSocketChat();

   const { currentUser } = useUser();

   // State to store user profiles for channels
   const [userProfiles, setUserProfiles] = useState<{
      [userId: string]: { displayName: string; photoURL: string | null };
   }>({});

   // Function to fetch user profile directly from database
   const fetchUserProfile = useCallback(async (userId: string) => {
      try {
         // First, try to get from userProfiles collection
         const userProfileDoc = await getDoc(doc(db, 'userProfiles', userId));
         if (userProfileDoc.exists()) {
            const profileData = userProfileDoc.data();

            return {
               displayName:
                  profileData.displayName ||
                  profileData.fullName ||
                  profileData.firstName ||
                  'Unknown User',
               photoURL:
                  profileData.photoURL || profileData.profilePicture || profileData.avatar || null,
            };
         }

         // Fallback to users collection
         const userDoc = await getDoc(doc(db, 'users', userId));
         if (userDoc.exists()) {
            const userData = userDoc.data();

            return {
               displayName:
                  userData.displayName || userData.fullName || userData.firstName || 'Unknown User',
               photoURL: userData.photoURL || userData.profilePicture || userData.avatar || null,
            };
         }

         return { displayName: 'Unknown User', photoURL: null };
      } catch (error) {
         console.error('Error fetching user profile:', error);
         return { displayName: 'Unknown User', photoURL: null };
      }
   }, []);

   // Utility function for dynamic chat name resolution - EXACT same logic as WebSocketChatView.tsx
   const getChannelDisplayName = useCallback(
      (channel: ChatRoom) => {
         const isDirectMessage = channel.isPrivate && channel.members.length === 2;

         // For direct messages, prioritize showing the other user's name
         if (isDirectMessage) {
            const otherUserId = channel.members.find((id) => id !== currentUser?.uid);
            if (otherUserId) {
               // First, try to get name from fetched user profiles
               const userProfile = userProfiles[otherUserId];
               if (userProfile && userProfile.displayName !== 'Unknown User') {
                  return userProfile.displayName;
               }

               // Get messages for this channel to help with name resolution
               const channelMessages = messages[channel.id] || [];

               // Try to get name from any message by this user
               const userMessage = channelMessages.find((msg) => msg.senderId === otherUserId);
               if (userMessage && userMessage.senderName) {
                  const senderName = userMessage.senderName;
                  if (senderName !== 'Unknown User' && !senderName.startsWith('User ')) {
                     return senderName;
                  }
               }

               // Try to get name from the last message if it's from the other user
               if (
                  channel.lastMessage &&
                  channel.lastMessage.senderId === otherUserId &&
                  channel.lastMessage.senderName
               ) {
                  const senderName = channel.lastMessage.senderName;
                  if (senderName !== 'Unknown User' && !senderName.startsWith('User ')) {
                     return senderName;
                  }
               }

               // Only use the stored name if it's a valid user name (not auto-generated)
               if (
                  channel.name &&
                  channel.name !== 'Unknown Chat' &&
                  !channel.name.startsWith('Room ') &&
                  !channel.name.startsWith('Chat with ') &&
                  !channel.name.includes('Unknown') &&
                  channel.name !== 'Direct Chat'
               ) {
                  return channel.name;
               }

               // Fallback to a more user-friendly format
               return `Chat with ${otherUserId.substring(0, 8)}...`;
            }
         }

         // For group chats, use the provided name
         if (
            channel.name &&
            channel.name !== 'Unknown Chat' &&
            !channel.name.startsWith('Room ') &&
            !channel.name.startsWith('Chat with ') &&
            !channel.name.includes('Unknown')
         ) {
            return channel.name;
         }

         // Final fallback
         return channel.name || `${isDirectMessage ? 'Direct Chat' : 'Group Chat'}`;
      },
      [currentUser?.uid, messages, userProfiles]
   );

   // Simple online status check using our service
   const isChannelUserOnline = useCallback(
      (channel: ChatRoom): boolean => {
         if (!currentUser || !channel.isPrivate || channel.members.length !== 2) {
            return false;
         }

         const otherUserId = channel.members.find((id) => id !== currentUser.uid);
         return otherUserId ? onlineStatusService.isUserOnline(otherUserId) : false;
      },
      [currentUser]
   );

   // State for chat functionality
   const [searchQuery, setSearchQuery] = useState('');
   const [activeTab, setActiveTab] = useState<'channels' | 'contacts' | 'entities'>('channels');
   const [filteredChannels, setFilteredChannels] = useState<ChatRoom[]>([]);
   const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
   const [contacts, setContacts] = useState<Contact[]>([]);
   const [loadingContacts, setLoadingContacts] = useState(false);
   const [loadingMoreContacts, setLoadingMoreContacts] = useState(false);
   const [showChannelList, setShowChannelList] = useState(true);
   const [createGroupChatDialogOpen, setCreateGroupChatDialogOpen] = useState(false);
   const [avatarsLoaded, setAvatarsLoaded] = useState(false);
   const [forceRender, setForceRender] = useState(0);

   // State to track channel avatars for floating chat list
   const [channelAvatars, setChannelAvatars] = useState<{ [channelId: string]: string | null }>({});
   const [contactAvatars, setContactAvatars] = useState<{ [userId: string]: string | null }>({});

   // Pagination state
   const [contactsPagination, setContactsPagination] = useState<PaginationState>({
      hasMore: true,
      lastDocs: {},
   });

   // Refs for infinite scroll
   const contactsListRef = React.useRef<HTMLDivElement>(null);
   const chatRoomsListRef = React.useRef<HTMLDivElement>(null);

   // Effect to refresh channels when the component loads
   useEffect(() => {
      if (client && connectionStatus === 'connected') {
         refreshChannels().catch((err) => {
            console.error('Failed to load chat rooms:', err);
         });
      }
   }, [client, connectionStatus, refreshChannels]);

   // Filter channels based on search query
   useEffect(() => {
      if (!searchQuery.trim()) {
         setFilteredChannels(channels);
         return;
      }

      const query = searchQuery.toLowerCase();
      const filtered = channels.filter((channel) => {
         const name = channel.name || '';
         const lastMessageText = channel.lastMessage?.text || '';
         const lastMessageSender = channel.lastMessage?.senderName || '';

         return (
            name.toLowerCase().includes(query) ||
            lastMessageText.toLowerCase().includes(query) ||
            lastMessageSender.toLowerCase().includes(query)
         );
      });

      setFilteredChannels(filtered);
   }, [channels, searchQuery, userProfiles, forceRender]);

   // Filter contacts based on search query
   useEffect(() => {
      if (!searchQuery.trim()) {
         setFilteredContacts(contacts);
         return;
      }

      const query = searchQuery.toLowerCase();
      const filtered = contacts.filter((contact) => {
         return (
            contact.displayName.toLowerCase().includes(query) ||
            contact.email.toLowerCase().includes(query)
         );
      });

      setFilteredContacts(filtered);
   }, [contacts, searchQuery]);

   // Load messages when a room is selected
   useEffect(() => {
      if (activeChannel && !messages[activeChannel.id]) {
         loadRoomMessages(activeChannel.id);
      }
   }, [activeChannel, messages, loadRoomMessages]);

   // Load user profiles and avatars for all channels when the component mounts and when channels change
   useEffect(() => {
      if (currentUser && channels.length > 0) {
         const loadUserProfilesForChannels = async () => {
            // Get all unique user IDs from all channels
            const userIds = new Set<string>();
            channels.forEach((channel) => {
               if (channel.isPrivate && channel.members.length === 2) {
                  const otherUserId = channel.members.find((id) => id !== currentUser.uid);
                  if (otherUserId) {
                     userIds.add(otherUserId);
                  }
               }
            });

            // Only fetch profiles for users we don't already have or need to refresh
            const userIdArray = Array.from(userIds);
            const usersToFetch = userIdArray.filter((userId) => {
               const existingProfile = userProfiles[userId];
               return !existingProfile || existingProfile.displayName === 'Unknown User';
            });

            // Preserve existing profiles and only add new ones
            const newUserProfiles: {
               [userId: string]: { displayName: string; photoURL: string | null };
            } = { ...userProfiles }; // Start with existing profiles

            for (const userId of usersToFetch) {
               try {
                  const profile = await fetchUserProfile(userId);
                  newUserProfiles[userId] = profile;
               } catch (error) {
                  console.error('❌ Error loading profile for user', userId, ':', error);
                  newUserProfiles[userId] = { displayName: 'Unknown User', photoURL: null };
               }
            }

            // Only update if we have new profiles to add
            if (usersToFetch.length > 0) {
               setUserProfiles(newUserProfiles);
               setForceRender((prev) => {
                  const newForceRender = prev + 1;
                  return newForceRender;
               });
            }

            setAvatarsLoaded(true);
         };

         if (!avatarsLoaded && currentUser && channels.length > 0) {
            loadUserProfilesForChannels();
         }
      }
   }, [channels.length, currentUser, fetchUserProfile, avatarsLoaded, channels, userProfiles]);

   // Reset view on mobile when active channel changes
   useEffect(() => {
      if (isMobile && showMainChat) {
         setShowChannelList(!activeChannel);
      }
   }, [activeChannel, isMobile, showMainChat]);

   // Fetch contacts from database
   const fetchContacts = async (isLoadMore = false) => {
      if (!currentUser) return;

      if (isLoadMore) {
         setLoadingMoreContacts(true);
      } else {
         setLoadingContacts(true);
         setContacts([]);
         setContactsPagination({
            hasMore: false, // Simplified - no pagination for now
            lastDocs: {},
         });
      }

      try {
         // Use the unified relationship_network endpoint
         const headers = await getAuthHeaders();
         const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/people/contacts?userId=${currentUser.uid}`,
            { headers }
         );

         if (response.data.success) {
            // Transform the relationship_network data to match Contact interface
            const contactsList = response.data.data
               .map((contact: any) => ({
                  id: contact.userId || contact.email || `contact_${Date.now()}`,
                  userId: contact.userId || '',
                  displayName: contact.displayName || contact.name || contact.email.split('@')[0],
                  email: contact.email,
                  photoURL: contact.photoURL || undefined,
                  source: 'relationship_network',
               }))
               .filter((contact: Contact) => contact.email && contact.email !== currentUser.email);

            console.log(
               'GlobalFloatingChat: Fetched contacts from relationship_network:',
               contactsList.length
            );

            // Update contacts (no load more for now - simplified)
            setContacts(contactsList);

            // Update pagination state
            setContactsPagination({
               hasMore: false, // Simplified - load all at once
               lastDocs: {},
            });
         } else {
            throw new Error(response.data.error || 'Failed to fetch contacts');
         }
      } catch (error: any) {
         console.error('Error fetching contacts from relationship_network:', error);
         // Set empty contacts on error
         setContacts([]);
      } finally {
         if (isLoadMore) {
            setLoadingMoreContacts(false);
         } else {
            setLoadingContacts(false);
         }
      }
   };

   // Fetch contacts when tab changes to contacts
   useEffect(() => {
      if (activeTab === 'contacts' && currentUser && (showMainChat || floatingChatListOpen)) {
         fetchContacts(false);
      }
   }, [activeTab, currentUser, showMainChat, floatingChatListOpen]);

   // Scroll handlers
   const handleContactsScroll = React.useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
         const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
         const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

         if (
            isNearBottom &&
            !loadingMoreContacts &&
            contactsPagination.hasMore &&
            contacts.length > 0
         ) {
            fetchContacts(true);
         }
      },
      [loadingMoreContacts, contactsPagination.hasMore, contacts.length]
   );

   const handleChatRoomsScroll = React.useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
         const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
         const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

         if (
            isNearBottom &&
            !roomsPagination.loading &&
            roomsPagination.hasMore &&
            channels.length > 0
         ) {
            loadMoreRooms();
         }
      },
      [roomsPagination.loading, roomsPagination.hasMore, channels.length, loadMoreRooms]
   );

   const handleTabChange = async (tab: 'channels' | 'contacts' | 'entities') => {
      setActiveTab(tab);
      setActiveChannel(null);
      setSearchQuery('');

      // Only reset avatars if we're switching to channels AND we have no channels or profiles loaded
      if (tab === 'channels') {
         // Reset avatars only if we have no channels or very few profiles loaded
         const hasMinimalProfiles = Object.keys(userProfiles).length < channels.length / 2;
         const shouldResetProfiles = channels.length === 0 || hasMinimalProfiles;

         if (shouldResetProfiles) {
            setAvatarsLoaded(false);
            // Don't clear existing profiles, just mark for reload
         }
      }

      if (tab === 'channels' && currentUser && (client || connectionStatus === 'connected')) {
         try {
            // Always refresh channels when switching to channels tab (not just when empty)
            await refreshChannels();
         } catch (err) {
            console.error('❌ Failed to load chat rooms:', err);
         }
      }
   };

   const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
   };

   const handleBackToList = () => {
      setActiveChannel(null);
      setShowChannelList(true);
   };

   const handleGroupChatCreated = async (createdChannel: ChatRoom) => {
      try {
         // Immediately add the new channel to the channels state
         setActiveTab('channels');

         // Add the new channel to the existing channels list
         const updatedChannels = [createdChannel, ...channels];

         // Set it as the active channel
         setActiveChannel(createdChannel);

         if (isMobile) {
            setShowChannelList(false);
         }

         // Refresh channels in the background to ensure sync
         setTimeout(() => {
            refreshChannels().catch(console.warn);
         }, 1000);
      } catch (error) {
         console.error('❌ Error handling group chat creation:', error);
         // Fallback: refresh channels
         refreshChannels();
      }
   };

   const handleRunDiagnostic = async () => {
      try {
         await clearCacheAndRefresh();
         await cleanupInvalidRooms();
      } catch (error) {
         console.error('❌ Diagnostic failed:', error);
      }
   };

   // Load avatars for channels and contacts using WebSocketService functions
   useEffect(() => {
      const loadAvatars = async () => {
         if (!currentUser) return;

         // Load channel avatars
         const newChannelAvatars: { [channelId: string]: string | null } = {};
         for (const channel of channels) {
            try {
               const avatarUrl = await webSocketService.getChatRoomAvatarUrl(
                  channel,
                  currentUser.uid
               );
               newChannelAvatars[channel.id] = avatarUrl;
            } catch (error) {
               console.warn(`Failed to load avatar for channel ${channel.id}:`, error);
               newChannelAvatars[channel.id] = null;
            }
         }
         setChannelAvatars(newChannelAvatars);

         // Load contact avatars
         const newContactAvatars: { [userId: string]: string | null } = {};
         for (const contact of contacts) {
            if (contact.userId) {
               try {
                  const avatarUrl = await webSocketService.getUserAvatarUrl(contact.userId);
                  newContactAvatars[contact.userId] = avatarUrl;
               } catch (error) {
                  console.warn(`Failed to load avatar for contact ${contact.userId}:`, error);
                  newContactAvatars[contact.userId] = null;
               }
            }
         }
         setContactAvatars(newContactAvatars);
      };

      loadAvatars();
   }, [channels, contacts, currentUser]);

   return (
      <>
         {/* Floating Chat Button */}
         <Fab
            color='primary'
            sx={{
               position: 'fixed',
               bottom: 80,
               right: 20,
               zIndex: 1300,
               display: floatingChatListOpen || showMainChat ? 'none' : 'flex',
            }}
            onClick={() => setFloatingChatListOpen(true)}
         >
            <ChatIcon />
         </Fab>
         {/* Main Chat Window (Full Screen) */}
         {showMainChat && (
            <Box
               sx={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1400,
                  bgcolor: 'background.default',
                  display: 'flex',
                  flexDirection: 'column',
               }}
            >
               {/* Main Chat Header */}
               <AppBar position='static' sx={{ bgcolor: 'primary.main' }}>
                  <Toolbar>
                     <IconButton
                        color='inherit'
                        onClick={() => setShowMainChat(false)}
                        edge='start'
                        sx={{ mr: 2 }}
                     >
                        <CloseIcon />
                     </IconButton>
                     <Typography variant='h6' sx={{ flexGrow: 1 }}>
                        Chat
                     </Typography>
                  </Toolbar>
               </AppBar>

               {/* Error and Status Messages */}
               {error && (
                  <Alert severity='error' sx={{ m: 2 }}>
                     {error.message}
                     {isFallbackMode && (
                        <Box sx={{ mt: 1 }}>
                           <Typography variant='body2' sx={{ mt: 1 }}>
                              <strong>Chat service is not available.</strong> This is usually due
                              to:
                           </Typography>
                           <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                              <li>Missing WebSocket API configuration</li>
                              <li>WebSocket server not running or failing</li>
                              <li>Network connectivity issues</li>
                           </ul>
                           <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <Button size='small' onClick={handleRunDiagnostic} variant='outlined'>
                                 Run Diagnostic
                              </Button>
                              <Button
                                 size='small'
                                 onClick={() => window.location.reload()}
                                 variant='outlined'
                              >
                                 Refresh Page
                              </Button>
                           </Box>
                        </Box>
                     )}
                  </Alert>
               )}

               {isFallbackMode && !error && (
                  <Alert severity='warning' sx={{ m: 2 }}>
                     Chat service is currently unavailable. Basic messaging features are disabled.
                  </Alert>
               )}

               {loading && !client && (
                  <Alert severity='info' sx={{ m: 2 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={20} sx={{ mr: 2 }} />
                        <Typography variant='body2'>
                           Connecting to chat service... Please wait before starting conversations.
                        </Typography>
                     </Box>
                  </Alert>
               )}

               {/* Main Chat Content */}
               <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 2 }}>
                  <Paper sx={{ height: '100%', overflow: 'hidden', borderRadius: 2 }}>
                     <Grid container sx={{ height: '100%' }}>
                        {/* Channels list */}
                        {(showChannelList || !isMobile) && (
                           <Grid
                              item
                              xs={12}
                              md={4}
                              sx={{
                                 height: '100%',
                                 borderRight: '1px solid',
                                 borderColor: 'divider',
                                 overflow: 'hidden',
                                 display: 'flex',
                                 flexDirection: 'column',
                              }}
                           >
                              <Box
                                 sx={{
                                    p: 2,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                 }}
                              >
                                 <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                       variant={activeTab === 'channels' ? 'contained' : 'outlined'}
                                       size='small'
                                       onClick={() => handleTabChange('channels')}
                                       startIcon={<ChatIcon />}
                                    >
                                       Chats
                                    </Button>
                                    <Button
                                       variant={activeTab === 'contacts' ? 'contained' : 'outlined'}
                                       size='small'
                                       onClick={() => handleTabChange('contacts')}
                                       startIcon={<PersonIcon />}
                                    >
                                       Contacts
                                    </Button>
                                    <Button
                                       variant={activeTab === 'entities' ? 'contained' : 'outlined'}
                                       size='small'
                                       onClick={() => handleTabChange('entities')}
                                       startIcon={<EventIcon />}
                                    >
                                       Events
                                    </Button>
                                 </Box>

                                 <TextField
                                    fullWidth
                                    size='small'
                                    placeholder='Search...'
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    InputProps={{
                                       startAdornment: (
                                          <InputAdornment position='start'>
                                             <SearchIcon fontSize='small' />
                                          </InputAdornment>
                                       ),
                                    }}
                                 />
                              </Box>

                              {/* Chat/Contact List */}
                              <Box sx={{ flexGrow: 1, overflow: 'auto', height: '70%' }}>
                                 {activeTab === 'channels' && (
                                    <>
                                       {loading ? (
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                p: 3,
                                             }}
                                          >
                                             <CircularProgress size={24} />
                                          </Box>
                                       ) : channels.length === 0 ? (
                                          <Box sx={{ p: 2 }}>
                                             <Typography color='text.secondary'>
                                                No chats yet. Start a new chat to get going!
                                             </Typography>
                                          </Box>
                                       ) : (
                                          <Box
                                             ref={chatRoomsListRef}
                                             sx={{ height: '100%', overflow: 'auto', mt: 2 }}
                                             onScroll={handleChatRoomsScroll}
                                          >
                                             {client && (
                                                <List disablePadding>
                                                   {filteredChannels.map((channel) => {
                                                      return (
                                                         <Box
                                                            key={`${channel.id}-${Object.keys(userProfiles).join(',')}`}
                                                         >
                                                            <CustomChannelPreview
                                                               channel={channel}
                                                               setActiveChannel={setActiveChannel}
                                                               activeChannel={activeChannel}
                                                               currentUser={currentUser}
                                                               onAvatarClick={openFloatingChat}
                                                               getChannelDisplayName={
                                                                  getChannelDisplayName
                                                               }
                                                               userProfiles={userProfiles}
                                                            />
                                                            <Divider component='li' />
                                                         </Box>
                                                      );
                                                   })}

                                                   {roomsPagination.loading && (
                                                      <Box
                                                         sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            p: 2,
                                                         }}
                                                      >
                                                         <CircularProgress size={20} />
                                                         <Typography
                                                            variant='body2'
                                                            sx={{ ml: 1 }}
                                                            color='text.secondary'
                                                         >
                                                            Loading more chats...
                                                         </Typography>
                                                      </Box>
                                                   )}

                                                   {!roomsPagination.hasMore &&
                                                      channels.length > 0 && (
                                                         <Box
                                                            sx={{
                                                               display: 'flex',
                                                               justifyContent: 'center',
                                                               p: 2,
                                                            }}
                                                         >
                                                            <Typography
                                                               variant='body2'
                                                               color='text.secondary'
                                                            >
                                                               All chats loaded
                                                            </Typography>
                                                         </Box>
                                                      )}
                                                </List>
                                             )}
                                          </Box>
                                       )}
                                    </>
                                 )}

                                 {activeTab === 'contacts' && (
                                    <>
                                       {loadingContacts ? (
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                p: 3,
                                             }}
                                          >
                                             <CircularProgress size={24} />
                                          </Box>
                                       ) : contacts.length === 0 ? (
                                          <Box sx={{ p: 2 }}>
                                             <Typography color='text.secondary'>
                                                No contacts found. Add contacts to chat with them!
                                             </Typography>
                                          </Box>
                                       ) : (
                                          <Box
                                             ref={contactsListRef}
                                             sx={{ height: '100%', overflow: 'auto' }}
                                             onScroll={handleContactsScroll}
                                          >
                                             <List disablePadding>
                                                {filteredContacts.map((contact) => (
                                                   <React.Fragment
                                                      key={`${contact.source}-${contact.id}`}
                                                   >
                                                      <Tooltip
                                                         title={`Contact: ${contact.displayName}`}
                                                         placement='right'
                                                      >
                                                         <ListItem
                                                            onClick={() => {
                                                               setSelectedContact(contact);
                                                               setFirstMessageModalOpen(true);
                                                            }}
                                                            sx={{
                                                               cursor: 'pointer',
                                                               '&:hover': {
                                                                  backgroundColor: 'action.hover',
                                                               },
                                                            }}
                                                         >
                                                            <ListItemAvatar>
                                                               <Badge
                                                                  overlap='circular'
                                                                  anchorOrigin={{
                                                                     vertical: 'bottom',
                                                                     horizontal: 'right',
                                                                  }}
                                                                  badgeContent={
                                                                     contact.userId ? (
                                                                        <OnlineIndicator
                                                                           userId={contact.userId}
                                                                           size='small'
                                                                        />
                                                                     ) : null
                                                                  }
                                                               >
                                                                  <Tooltip title='Click to open floating chat'>
                                                                     <Avatar
                                                                        alt={contact.displayName}
                                                                        src={contact.photoURL}
                                                                        sx={{
                                                                           cursor: 'pointer',
                                                                           '&:hover': {
                                                                              transform:
                                                                                 'scale(1.1)',
                                                                              transition:
                                                                                 'transform 0.2s ease-in-out',
                                                                           },
                                                                        }}
                                                                        onClick={(e) => {
                                                                           e.stopPropagation();
                                                                           openFloatingChatForContact(
                                                                              contact
                                                                           );
                                                                        }}
                                                                     >
                                                                        {contact.displayName?.[0]?.toUpperCase() ||
                                                                           'U'}
                                                                     </Avatar>
                                                                  </Tooltip>
                                                               </Badge>
                                                            </ListItemAvatar>
                                                            <ListItemText
                                                               primary={contact.displayName}
                                                               secondary={
                                                                  <Box>
                                                                     <Typography
                                                                        variant='body2'
                                                                        color='text.secondary'
                                                                     >
                                                                        {contact.email}
                                                                     </Typography>
                                                                     {contact.source && (
                                                                        <Chip
                                                                           label={
                                                                              contact.source ===
                                                                              'relationship'
                                                                                 ? 'Contact'
                                                                                 : contact.source ===
                                                                                     'event_invitation'
                                                                                   ? 'Event Invite Sent'
                                                                                   : 'Trip Invite Sent'
                                                                           }
                                                                           size='small'
                                                                           variant='outlined'
                                                                           sx={{
                                                                              mt: 0.5,
                                                                              fontSize: '0.7rem',
                                                                              height: 20,
                                                                           }}
                                                                        />
                                                                     )}
                                                                  </Box>
                                                               }
                                                            />
                                                         </ListItem>
                                                      </Tooltip>
                                                      <Divider component='li' />
                                                   </React.Fragment>
                                                ))}

                                                {loadingMoreContacts && (
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         justifyContent: 'center',
                                                         p: 2,
                                                      }}
                                                   >
                                                      <CircularProgress size={20} />
                                                      <Typography
                                                         variant='body2'
                                                         sx={{ ml: 1 }}
                                                         color='text.secondary'
                                                      >
                                                         Loading more contacts...
                                                      </Typography>
                                                   </Box>
                                                )}
                                             </List>
                                          </Box>
                                       )}
                                    </>
                                 )}

                                 {activeTab === 'entities' && (
                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                       <Typography variant='h6' gutterBottom>
                                          Event & Trip Group Chats
                                       </Typography>
                                       <Typography variant='body2' color='text.secondary' paragraph>
                                          Create group chats for your events and trips. All
                                          participants will be automatically invited to join.
                                       </Typography>

                                       <Button
                                          variant='contained'
                                          startIcon={<GroupIcon />}
                                          onClick={() => setCreateGroupChatDialogOpen(true)}
                                          sx={{ mb: 2 }}
                                       >
                                          Create Group Chat
                                       </Button>

                                       <Card variant='outlined' sx={{ mt: 2 }}>
                                          <CardContent>
                                             <Typography variant='subtitle2' gutterBottom>
                                                How it works:
                                             </Typography>
                                             <Typography variant='body2' color='text.secondary'>
                                                • Select an event or trip from your list
                                                <br />
                                                • A group chat will be created automatically
                                                <br />
                                                • All participants will be invited to join
                                                <br />• Group chats appear in your regular chat list
                                             </Typography>
                                          </CardContent>
                                       </Card>
                                    </Box>
                                 )}
                              </Box>
                           </Grid>
                        )}

                        {/* Active channel */}
                        {(!showChannelList || !isMobile) && (
                           <Grid
                              item
                              xs={12}
                              md={8}
                              sx={{
                                 height: '100%',
                                 display: 'flex',
                                 flexDirection: 'column',
                              }}
                           >
                              {isMobile && (
                                 <Box
                                    sx={{
                                       p: 1,
                                       borderBottom: '1px solid',
                                       borderColor: 'divider',
                                       display: 'flex',
                                       alignItems: 'center',
                                    }}
                                 >
                                    <IconButton onClick={handleBackToList}>
                                       <ArrowBackIcon />
                                    </IconButton>
                                    <Typography sx={{ ml: 1 }}>
                                       {activeChannel
                                          ? getChannelDisplayName(activeChannel)
                                          : 'Chat'}
                                    </Typography>
                                 </Box>
                              )}

                              {activeChannel ? (
                                 <WebSocketChatView room={activeChannel} />
                              ) : (
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       flexDirection: 'column',
                                       justifyContent: 'center',
                                       alignItems: 'center',
                                       height: '100%',
                                       p: 3,
                                       bgcolor: 'background.default',
                                    }}
                                 >
                                    <ChatIcon
                                       sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }}
                                    />
                                    <Typography variant='h6' color='text.secondary' gutterBottom>
                                       No chat selected
                                    </Typography>
                                    <Typography color='text.secondary' align='center'>
                                       Select a chat from the list or start a new conversation
                                    </Typography>
                                 </Box>
                              )}
                           </Grid>
                        )}
                     </Grid>
                  </Paper>
               </Box>
            </Box>
         )}
         {/* Floating Chat List Window */}
         <Box
            sx={{
               position: 'fixed',
               bottom: 8,
               right: 0,
               width: 400,
               height: floatingChatListMinimized ? 60 : 500,
               zIndex: 1300,
               display: floatingChatListOpen && !showMainChat ? 'block' : 'none',
               borderRadius: '8px 8px 0 0',
               boxShadow: 3,
               bgcolor: 'background.paper',
               border: '1px solid',
               borderColor: 'divider',
               overflow: 'hidden',
               transition: 'height 0.3s ease-in-out',
            }}
         >
            {/* Floating Chat Header */}
            <AppBar
               position='static'
               sx={{
                  bgcolor: 'primary.main',
                  boxShadow: 'none',
                  borderRadius: '8px 8px 0 0',
               }}
            >
               <Toolbar variant='dense' sx={{ minHeight: 48 }}>
                  <ChatIcon sx={{ mr: 1 }} />

                  <IconButton
                     color='inherit'
                     size='small'
                     onClick={() => setFloatingChatListMinimized(!floatingChatListMinimized)}
                  >
                     {floatingChatListMinimized ? <ExpandLessIcon /> : <MinimizeIcon />}
                  </IconButton>
                  <IconButton
                     color='inherit'
                     size='small'
                     onClick={() => setFloatingChatListOpen(false)}
                  >
                     <CloseIcon />
                  </IconButton>
               </Toolbar>
            </AppBar>

            {/* Floating Chat Content */}
            <Collapse in={!floatingChatListMinimized}>
               <Box sx={{ height: 452, display: 'flex', flexDirection: 'column' }}>
                  {/* Chat Tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 1 }}>
                     <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                           variant={activeTab === 'channels' ? 'contained' : 'outlined'}
                           size='small'
                           onClick={() => handleTabChange('channels')}
                           sx={{ fontSize: '0.75rem', minWidth: 60 }}
                        >
                           Chats
                        </Button>
                        <Button
                           variant={activeTab === 'contacts' ? 'contained' : 'outlined'}
                           size='small'
                           onClick={() => handleTabChange('contacts')}
                           sx={{ fontSize: '0.75rem', minWidth: 60 }}
                        >
                           Contacts
                        </Button>
                        <Button
                           variant={activeTab === 'entities' ? 'contained' : 'outlined'}
                           size='small'
                           onClick={() => handleTabChange('entities')}
                           sx={{ fontSize: '0.75rem', minWidth: 50 }}
                        >
                           Events
                        </Button>
                     </Box>
                  </Box>

                  {/* Chat List */}
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                     {/* Search */}
                     <Box sx={{ p: 1 }}>
                        <TextField
                           fullWidth
                           size='small'
                           placeholder={`Search ${activeTab === 'channels' ? 'chats' : 'contacts'}...`}
                           value={searchQuery}
                           onChange={handleSearchChange}
                           InputProps={{
                              startAdornment: (
                                 <InputAdornment position='start'>
                                    <SearchIcon fontSize='small' />
                                 </InputAdornment>
                              ),
                           }}
                        />
                     </Box>

                     {activeTab === 'channels' && (
                        <>
                           {loading ? (
                              <Box sx={{ p: 2, textAlign: 'center' }}>
                                 <CircularProgress size={20} />
                                 <Typography
                                    variant='caption'
                                    sx={{ mt: 1, display: 'block' }}
                                    color='text.secondary'
                                 >
                                    Loading chats...
                                 </Typography>
                              </Box>
                           ) : filteredChannels.length === 0 ? (
                              <Box sx={{ p: 2, textAlign: 'center' }}>
                                 <Typography variant='body2' color='text.secondary'>
                                    No chats yet
                                 </Typography>
                              </Box>
                           ) : (
                              <Box
                                 sx={{
                                    height: '80%',
                                    overflow: 'auto',
                                    '&::-webkit-scrollbar': {
                                       width: '4px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                       backgroundColor: 'rgba(0,0,0,0.1)',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                       backgroundColor: 'rgba(0,0,0,0.2)',
                                       borderRadius: '2px',
                                    },
                                 }}
                                 onScroll={handleChatRoomsScroll}
                              >
                                 <List dense>
                                    {filteredChannels.map((channel) => {
                                       const isFloatingOpen = floatingChats.some(
                                          (fc) => fc.channel.id === channel.id
                                       );
                                       return (
                                          <ListItem
                                             key={channel.id}
                                             sx={{
                                                py: 0.5,
                                                px: 1,
                                                cursor: 'pointer',
                                                backgroundColor: isFloatingOpen
                                                   ? 'action.selected'
                                                   : 'transparent',
                                                '&:hover': {
                                                   backgroundColor: isFloatingOpen
                                                      ? 'action.selected'
                                                      : 'action.hover',
                                                },
                                             }}
                                             onClick={() => openFloatingChat(channel)}
                                          >
                                             <ListItemAvatar>
                                                <Badge
                                                   overlap='circular'
                                                   anchorOrigin={{
                                                      vertical: 'top',
                                                      horizontal: 'right',
                                                   }}
                                                   badgeContent={
                                                      channel.isPrivate &&
                                                      channel.members.length === 2
                                                         ? (() => {
                                                              const otherUserId =
                                                                 channel.members.find(
                                                                    (id) => id !== currentUser?.uid
                                                                 );
                                                              return otherUserId ? (
                                                                 <OnlineIndicator
                                                                    userId={otherUserId}
                                                                    size='small'
                                                                 />
                                                              ) : null;
                                                           })()
                                                         : null
                                                   }
                                                >
                                                   <Avatar
                                                      src={(() => {
                                                         // Get avatar URL for the channel
                                                         let avatarUrl = channel.avatarUrl;

                                                         // For direct messages, try to get user profile photo
                                                         if (
                                                            channel.isPrivate &&
                                                            channel.members.length === 2
                                                         ) {
                                                            const otherUserId =
                                                               channel.members.find(
                                                                  (id) => id !== currentUser?.uid
                                                               );
                                                            if (
                                                               otherUserId &&
                                                               userProfiles[otherUserId]
                                                            ) {
                                                               avatarUrl =
                                                                  userProfiles[otherUserId]
                                                                     .photoURL || avatarUrl;
                                                            }
                                                            // If still no avatar, create a fallback using initials
                                                            if (
                                                               !avatarUrl &&
                                                               otherUserId &&
                                                               userProfiles[otherUserId]
                                                            ) {
                                                               const displayName =
                                                                  userProfiles[otherUserId]
                                                                     .displayName;
                                                               const initials = displayName
                                                                  .split(' ')
                                                                  .map((word) => word[0])
                                                                  .join('')
                                                                  .toUpperCase()
                                                                  .substring(0, 2);
                                                               avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=random&color=fff&size=128`;
                                                            }
                                                         }

                                                         return avatarUrl;
                                                      })()}
                                                      sx={{
                                                         width: 32,
                                                         height: 32,
                                                         cursor: 'pointer',
                                                         '&:hover': {
                                                            transform: 'scale(1.1)',
                                                            transition:
                                                               'transform 0.2s ease-in-out',
                                                         },
                                                      }}
                                                   >
                                                      {channel.isPrivate ? (
                                                         (() => {
                                                            // Get initials for direct message fallback
                                                            const otherUserId =
                                                               channel.members.find(
                                                                  (id) => id !== currentUser?.uid
                                                               );
                                                            if (
                                                               otherUserId &&
                                                               userProfiles[otherUserId]
                                                            ) {
                                                               const displayName =
                                                                  userProfiles[otherUserId]
                                                                     .displayName;
                                                               return displayName
                                                                  .substring(0, 2)
                                                                  .toUpperCase();
                                                            }
                                                            return <PersonIcon />;
                                                         })()
                                                      ) : (
                                                         <GroupIcon />
                                                      )}
                                                   </Avatar>
                                                </Badge>
                                             </ListItemAvatar>
                                             <ListItemText
                                                primary={
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                      }}
                                                   >
                                                      <Typography variant='body2' noWrap>
                                                         {getChannelDisplayName(channel)}
                                                      </Typography>
                                                   </Box>
                                                }
                                                secondary={
                                                   <Typography
                                                      variant='caption'
                                                      color='text.secondary'
                                                      noWrap
                                                   >
                                                      {channel.lastMessage?.text || 'No messages'}
                                                   </Typography>
                                                }
                                             />
                                             {(channel.unreadCount || 0) > 0 && (
                                                <Badge
                                                   badgeContent={channel.unreadCount}
                                                   color='primary'
                                                   sx={{
                                                      '& .MuiBadge-badge': {
                                                         fontSize: '0.6rem',
                                                      },
                                                   }}
                                                />
                                             )}
                                          </ListItem>
                                       );
                                    })}

                                    {/* Loading more channels indicator */}
                                    {roomsPagination.loading && (
                                       <Box
                                          sx={{
                                             display: 'flex',
                                             justifyContent: 'center',
                                             p: 1,
                                          }}
                                       >
                                          <CircularProgress size={16} />
                                          <Typography
                                             variant='caption'
                                             sx={{ ml: 1 }}
                                             color='text.secondary'
                                          >
                                             Loading more chats...
                                          </Typography>
                                       </Box>
                                    )}

                                    {/* End of channels indicator */}
                                    {!roomsPagination.hasMore && channels.length > 10 && (
                                       <Box
                                          sx={{
                                             display: 'flex',
                                             justifyContent: 'center',
                                             p: 1,
                                          }}
                                       >
                                          <Typography variant='caption' color='text.secondary'>
                                             All chats loaded
                                          </Typography>
                                       </Box>
                                    )}
                                 </List>
                              </Box>
                           )}
                        </>
                     )}

                     {activeTab === 'contacts' && (
                        <>
                           {loadingContacts ? (
                              <Box sx={{ p: 2, textAlign: 'center' }}>
                                 <CircularProgress size={20} />
                                 <Typography
                                    variant='caption'
                                    sx={{ mt: 1, display: 'block' }}
                                    color='text.secondary'
                                 >
                                    Loading contacts...
                                 </Typography>
                              </Box>
                           ) : filteredContacts.length === 0 ? (
                              <Box sx={{ p: 2, textAlign: 'center' }}>
                                 <Typography variant='body2' color='text.secondary'>
                                    No contacts found
                                 </Typography>
                              </Box>
                           ) : (
                              <Box
                                 sx={{
                                    height: '100%',
                                    overflow: 'auto',
                                    '&::-webkit-scrollbar': {
                                       width: '4px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                       backgroundColor: 'rgba(0,0,0,0.1)',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                       backgroundColor: 'rgba(0,0,0,0.2)',
                                       borderRadius: '2px',
                                    },
                                 }}
                                 onScroll={handleContactsScroll}
                              >
                                 <List dense>
                                    {filteredContacts.map((contact) => {
                                       const isFloatingOpen = floatingChats.some(
                                          (fc) =>
                                             fc.channel.members.includes(contact.userId) &&
                                             fc.channel.isPrivate &&
                                             fc.channel.members.length === 2
                                       );
                                       return (
                                          <ListItem
                                             key={`${contact.source}-${contact.id}`}
                                             sx={{
                                                py: 0.5,
                                                px: 1,
                                                cursor: 'pointer',
                                                backgroundColor: isFloatingOpen
                                                   ? 'action.selected'
                                                   : 'transparent',
                                                '&:hover': {
                                                   backgroundColor: isFloatingOpen
                                                      ? 'action.selected'
                                                      : 'action.hover',
                                                },
                                             }}
                                             onClick={() => {
                                                setSelectedContact(contact);
                                                setFirstMessageModalOpen(true);
                                             }}
                                          >
                                             <ListItemAvatar>
                                                <Badge
                                                   overlap='circular'
                                                   anchorOrigin={{
                                                      vertical: 'top',
                                                      horizontal: 'right',
                                                   }}
                                                   badgeContent={
                                                      contact.userId ? (
                                                         <OnlineIndicator
                                                            userId={contact.userId}
                                                            size='small'
                                                         />
                                                      ) : null
                                                   }
                                                >
                                                   <Tooltip title='Click to open floating chat'>
                                                      <Avatar
                                                         src={
                                                            contactAvatars[contact.userId] ||
                                                            contact.photoURL ||
                                                            undefined
                                                         }
                                                         sx={{
                                                            width: 32,
                                                            height: 32,
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                               transform: 'scale(1.1)',
                                                               transition:
                                                                  'transform 0.2s ease-in-out',
                                                            },
                                                         }}
                                                         onClick={(e) => {
                                                            e.stopPropagation();
                                                            openFloatingChatForContact(contact);
                                                         }}
                                                      >
                                                         {contact.displayName?.[0]?.toUpperCase() ||
                                                            'U'}
                                                      </Avatar>
                                                   </Tooltip>
                                                </Badge>
                                             </ListItemAvatar>
                                             <ListItemText
                                                primary={
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                      }}
                                                   >
                                                      <Typography variant='body2' noWrap>
                                                         {contact.displayName}
                                                      </Typography>
                                                      {isFloatingOpen && (
                                                         <Chip
                                                            label='Open'
                                                            size='small'
                                                            color='success'
                                                            sx={{
                                                               ml: 1,
                                                               height: 16,
                                                               fontSize: '0.6rem',
                                                            }}
                                                         />
                                                      )}
                                                   </Box>
                                                }
                                                secondary={
                                                   <Box>
                                                      <Typography
                                                         variant='caption'
                                                         color='text.secondary'
                                                         noWrap
                                                      >
                                                         {contact.email}
                                                      </Typography>
                                                      {contact.source && (
                                                         <Chip
                                                            label={
                                                               contact.source === 'relationship'
                                                                  ? 'Contact'
                                                                  : contact.source ===
                                                                      'event_invitation'
                                                                    ? 'Event Invite'
                                                                    : 'Trip Invite'
                                                            }
                                                            size='small'
                                                            variant='outlined'
                                                            sx={{
                                                               ml: 0.5,
                                                               mt: 0.5,
                                                               fontSize: '0.6rem',
                                                               height: 16,
                                                            }}
                                                         />
                                                      )}
                                                   </Box>
                                                }
                                             />
                                          </ListItem>
                                       );
                                    })}

                                    {/* Loading more contacts indicator */}
                                    {loadingMoreContacts && (
                                       <Box
                                          sx={{
                                             display: 'flex',
                                             justifyContent: 'center',
                                             p: 1,
                                          }}
                                       >
                                          <CircularProgress size={16} />
                                          <Typography
                                             variant='caption'
                                             sx={{ ml: 1 }}
                                             color='text.secondary'
                                          >
                                             Loading more...
                                          </Typography>
                                       </Box>
                                    )}

                                    {/* End of contacts indicator */}
                                    {!contactsPagination.hasMore && contacts.length > 10 && (
                                       <Box
                                          sx={{
                                             display: 'flex',
                                             justifyContent: 'center',
                                             p: 1,
                                          }}
                                       >
                                          <Typography variant='caption' color='text.secondary'>
                                             All contacts loaded
                                          </Typography>
                                       </Box>
                                    )}
                                 </List>
                              </Box>
                           )}
                        </>
                     )}

                     {activeTab === 'entities' && (
                        <>
                           <Box sx={{ p: 2, textAlign: 'center' }}>
                              <Typography variant='body2' color='text.secondary' gutterBottom>
                                 Create group chats for events and trips
                              </Typography>
                              <Button
                                 variant='contained'
                                 size='small'
                                 startIcon={<GroupIcon />}
                                 onClick={() => setCreateGroupChatDialogOpen(true)}
                                 sx={{ fontSize: '0.75rem' }}
                              >
                                 Create Group Chat
                              </Button>
                           </Box>
                        </>
                     )}
                  </Box>
               </Box>
            </Collapse>
         </Box>
         ;{/* Horizontal Scrollable Container for Individual Floating Chat Windows */}
         <Box
            sx={{
               position: 'fixed',
               bottom: 8,
               left: 0,
               right: 420,
               height: 500,
               zIndex: 1200,
               display: floatingChats.length > 0 && !showMainChat ? 'block' : 'none',
               overflowX: 'auto',
               overflowY: 'hidden',
               '&::-webkit-scrollbar': {
                  height: 8,
               },
               '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
               },
               '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  borderRadius: '4px',
               },
            }}
         >
            <Box
               sx={{
                  justifyContent: 'flex-end',
                  display: 'flex',
                  height: '100%',
                  minWidth: 'fit-content',
                  gap: 1,
                  pl: 1,
               }}
            >
               {/* Individual Floating Chat Windows */}
               {floatingChats.map((floatingChat, index) => {
                  // Dynamic name resolution for floating chat headers
                  const displayName = getChannelDisplayName(floatingChat.channel);

                  return (
                     <Box
                        key={floatingChat.id}
                        sx={{
                           width: 400,
                           height: floatingChat.minimized ? 60 : '100%',
                           borderRadius: '8px 8px 0 0',
                           boxShadow: 3,
                           bgcolor: 'background.paper',
                           border: '1px solid',
                           borderColor: 'divider',
                           overflow: 'hidden',
                           transition: 'height 0.3s ease-in-out',
                           flexShrink: 0,
                        }}
                     >
                        {/* Individual Chat Header */}
                        <AppBar
                           position='static'
                           sx={{
                              bgcolor: 'primary.main',
                              boxShadow: 'none',
                              borderRadius: '8px 8px 0 0',
                           }}
                        >
                           <Toolbar variant='dense' sx={{ minHeight: 48 }}>
                              <IconButton
                                 color='inherit'
                                 size='small'
                                 onClick={() => toggleFloatingChat(floatingChat.id)}
                              >
                                 {floatingChat.minimized ? <ExpandLessIcon /> : <MinimizeIcon />}
                              </IconButton>
                              <IconButton
                                 color='inherit'
                                 size='small'
                                 onClick={() => closeFloatingChat(floatingChat.id)}
                              >
                                 <CloseIcon />
                              </IconButton>
                           </Toolbar>
                        </AppBar>

                        {/* Individual Chat Content */}
                        <Collapse in={!floatingChat.minimized}>
                           <Box sx={{ height: 452 }}>
                              <WebSocketChatView room={floatingChat.channel} />
                           </Box>
                        </Collapse>
                     </Box>
                  );
               })}
            </Box>
         </Box>
         ;{/* Create Group Chat Dialog */}
         <CreateGroupChatDialog
            open={createGroupChatDialogOpen}
            onClose={() => setCreateGroupChatDialogOpen(false)}
            onGroupChatCreated={handleGroupChatCreated}
         />
         {/* First Message Modal */}
         <Dialog
            open={firstMessageModalOpen}
            onClose={handleCloseFirstMessageModal}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={selectedContact?.photoURL} sx={{ width: 40, height: 40 }}>
                     {selectedContact?.displayName?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box>
                     <Typography variant='h6'>
                        Send message to {selectedContact?.displayName}
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        {selectedContact?.email}
                     </Typography>
                  </Box>
               </Box>
            </DialogTitle>
            <DialogContent>
               <TextField
                  autoFocus
                  multiline
                  rows={4}
                  fullWidth
                  placeholder='Type your message...'
                  value={firstMessage}
                  onChange={(e) => setFirstMessage(e.target.value)}
                  variant='outlined'
                  sx={{ mt: 1 }}
                  disabled={sendingFirstMessage}
               />
               {sendingFirstMessage && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                     <CircularProgress size={16} />
                     <Typography variant='body2' color='text.secondary'>
                        Sending your message...
                     </Typography>
                  </Box>
               )}
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCloseFirstMessageModal} disabled={sendingFirstMessage}>
                  Cancel
               </Button>
               <Button
                  onClick={handleSendFirstMessage}
                  variant='contained'
                  startIcon={sendingFirstMessage ? <CircularProgress size={16} /> : <SendIcon />}
                  disabled={!firstMessage.trim() || sendingFirstMessage}
               >
                  {sendingFirstMessage ? 'Sending...' : 'Send Message'}
               </Button>
            </DialogActions>
         </Dialog>
         {/* Chat Message Snackbar */}
         <Snackbar
            open={showExistingChatMessage}
            autoHideDuration={6000}
            onClose={() => setShowExistingChatMessage(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setShowExistingChatMessage(false)}
               severity={
                  existingChatMessage?.includes('Failed') || existingChatMessage?.includes('Error')
                     ? 'error'
                     : existingChatMessage?.includes('sent')
                       ? 'success'
                       : 'info'
               }
               variant='filled'
               sx={{ width: '100%' }}
            >
               {existingChatMessage}
            </Alert>
         </Snackbar>
      </>
   );
};

export default GlobalFloatingChat;
